import { ROLE_ID_OPERACIONES } from '../constants/roleIds'
import {
  getProductEcommerceStock,
  parseProductStockField
} from './productStockDisplayUtils'

/** Rol 16 — manager / operaciones Homecenter: BHOMEC en lugar de XEROX en lista de productos */
export function isManagerHomecenterProductListRole(
  roleId: number | undefined | null
): boolean {
  return roleId === ROLE_ID_OPERACIONES
}

export type OfimaBodegaDisponibleField =
  | 'Disponible_BODTEMPORAL'
  | 'Disponible_BODGSM'
  | 'Disponible_BXEROX'
  | 'Disponible_BHOMEC'

export type ProductTableStockFilterKey = 'ecommerce' | OfimaBodegaDisponibleField

export interface ProductTableBodegaColumn {
  header: string
  filterKey: ProductTableStockFilterKey
  key: string
  positiveClass: string
}

/** Campos Ofima usados en GET /products/ofima/all (subset para stock por bodega). */
export interface OfimaProductoStockFields {
  SaldoDisponible: number
  Disponible_BODTEMPORAL?: number
  Disponible_BODGSM?: number
  Disponible_BXEROX?: number
  Disponible_BHOMEC?: number
}

const ECOMMERCE_COLUMN: ProductTableBodegaColumn = {
  header: 'Ecommerce',
  filterKey: 'ecommerce',
  key: 'ecommerce_stock_col',
  positiveClass: 'bg-blue-100 text-blue-800'
}

const WAREHOUSE_COLUMNS_STANDARD: ProductTableBodegaColumn[] = [
  {
    header: 'Temporal',
    filterKey: 'Disponible_BODTEMPORAL',
    key: 'bod_temporal_col',
    positiveClass: 'bg-purple-100 text-purple-800'
  },
  {
    header: 'GSM',
    filterKey: 'Disponible_BODGSM',
    key: 'bod_gsm_col',
    positiveClass: 'bg-amber-100 text-amber-900'
  },
  {
    header: 'XEROX',
    filterKey: 'Disponible_BXEROX',
    key: 'bod_xerox_col',
    positiveClass: 'bg-teal-100 text-teal-800'
  }
]

const WAREHOUSE_COLUMNS_HOMECENTER: ProductTableBodegaColumn[] = [
  {
    header: 'Temporal',
    filterKey: 'Disponible_BODTEMPORAL',
    key: 'bod_temporal_col',
    positiveClass: 'bg-purple-100 text-purple-800'
  },
  {
    header: 'GSM',
    filterKey: 'Disponible_BODGSM',
    key: 'bod_gsm_col',
    positiveClass: 'bg-amber-100 text-amber-900'
  },
  {
    header: 'BHOMEC',
    filterKey: 'Disponible_BHOMEC',
    key: 'bod_bhomec_col',
    positiveClass: 'bg-rose-100 text-rose-900'
  }
]

export function getProductTableBodegaColumns(
  roleId: number | undefined | null
): ProductTableBodegaColumn[] {
  const warehouses = isManagerHomecenterProductListRole(roleId)
    ? WAREHOUSE_COLUMNS_HOMECENTER
    : WAREHOUSE_COLUMNS_STANDARD
  return [ECOMMERCE_COLUMN, ...warehouses]
}

export function getDefaultSelectedBodegas(
  roleId: number | undefined | null
): ProductTableStockFilterKey[] {
  return getProductTableBodegaColumns(roleId).map((col) => col.filterKey)
}

export function getBodegaFilterOptions(roleId: number | undefined | null) {
  return getProductTableBodegaColumns(roleId).map(({ header, filterKey }) => ({
    label: header,
    value: filterKey
  }))
}

export function getValidBodegaFilterKeys(
  roleId: number | undefined | null
): Set<ProductTableStockFilterKey> {
  return new Set(getDefaultSelectedBodegas(roleId))
}

export function isDefaultBodegasSelection(
  roleId: number | undefined | null,
  selected: readonly ProductTableStockFilterKey[]
): boolean {
  const defaults = getDefaultSelectedBodegas(roleId)
  return (
    selected.length === defaults.length &&
    defaults.every((field) => selected.includes(field))
  )
}

/** Mantiene solo claves válidas para el rol; bodegas de otro rol reinician el default completo. */
export function sanitizeSelectedBodegasForRole(
  roleId: number | undefined | null,
  selected: readonly ProductTableStockFilterKey[]
): ProductTableStockFilterKey[] {
  const allowed = getValidBodegaFilterKeys(roleId)
  const isHomecenter = isManagerHomecenterProductListRole(roleId)
  const hasForeignBodega = selected.some(
    (key) =>
      !allowed.has(key) ||
      (isHomecenter && key === 'Disponible_BXEROX') ||
      (!isHomecenter && key === 'Disponible_BHOMEC')
  )
  if (hasForeignBodega) {
    return getDefaultSelectedBodegas(roleId)
  }
  const filtered = selected.filter((key) => allowed.has(key))
  return filtered.length > 0 ? [...filtered] : getDefaultSelectedBodegas(roleId)
}

function parseOfimaDisponibleValue(raw: unknown): number | null {
  if (raw === undefined || raw === null) return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  return n
}

export function getOfimaStockFilterValue(
  productoOfima: OfimaProductoStockFields | undefined,
  filterKey: ProductTableStockFilterKey
): number | null {
  if (!productoOfima) return null
  if (filterKey === 'ecommerce') {
    return parseOfimaDisponibleValue(productoOfima.SaldoDisponible)
  }
  return parseOfimaDisponibleValue(productoOfima[filterKey])
}

function sumOfimaBodegasToStock(
  productoOfima: OfimaProductoStockFields | undefined,
  stockFilterKeys: readonly ProductTableStockFilterKey[]
): number {
  if (!productoOfima || stockFilterKeys.length === 0) return 0
  return stockFilterKeys.reduce((acc, filterKey) => {
    return acc + (getOfimaStockFilterValue(productoOfima, filterKey) ?? 0)
  }, 0)
}

export function getApiStockForBodegaFilter(
  product: Record<string, unknown>,
  filterKey: ProductTableStockFilterKey
): number {
  switch (filterKey) {
    case 'ecommerce':
      return getProductEcommerceStock(product)
    case 'Disponible_BODTEMPORAL':
      return parseProductStockField(product.stock_bodtemporal)
    case 'Disponible_BODGSM':
      return parseProductStockField(product.stock_gsm)
    case 'Disponible_BXEROX':
      return parseProductStockField(product.stock_xerox)
    case 'Disponible_BHOMEC':
      return parseProductStockField(product.stock_bhomec)
    default:
      return 0
  }
}

function sumApiBodegasToStock(
  product: Record<string, unknown>,
  stockFilterKeys: readonly ProductTableStockFilterKey[]
): number {
  if (stockFilterKeys.length === 0) return 0
  return stockFilterKeys.reduce(
    (acc, filterKey) => acc + getApiStockForBodegaFilter(product, filterKey),
    0
  )
}

/**
 * Stock = solo bodegas seleccionadas en filtro.
 * Ofima: SaldoDisponible + Disponible_* | API: stock_ecomerce, stock_bodtemporal, etc.
 */
export function getStockDisplayValueForBodegas(
  productoOfima: OfimaProductoStockFields | undefined,
  apiProduct: Record<string, unknown>,
  stockFilterKeys: readonly ProductTableStockFilterKey[],
  validKeys: Set<ProductTableStockFilterKey>
): number {
  const activeKeys = stockFilterKeys.filter((key) => validKeys.has(key))
  if (activeKeys.length === 0) return 0
  if (productoOfima) {
    return sumOfimaBodegasToStock(productoOfima, activeKeys)
  }
  return sumApiBodegasToStock(apiProduct, activeKeys)
}
